import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { useSermonSeries } from '../hooks/useSermonSeries';
import { useSermons } from '../hooks/useSermons';
import { useUpsertSermon } from '../hooks/useUpsertSermon';
import { supabase } from '../services/supabase';
import { useSermonAssets } from '../hooks/useSermonAssets';

const Page = styled.div`
  min-height: 100%;
  padding: 1.5rem 1.25rem 2.5rem;
  background: linear-gradient(
    165deg,
    color-mix(in srgb, var(--primary) 6%, var(--background)) 0%,
    var(--background) 42%,
    var(--background) 100%
  );
`;

const PageInner = styled.div`
  max-width: 820px;
  margin: 0 auto;
`;

const BackRow = styled.div`
  margin-bottom: 1.25rem;
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--primary);
  text-decoration: none;
  padding: 0.35rem 0.5rem 0.35rem 0;
  border-radius: 8px;
  transition: color 0.15s ease, background 0.15s ease;
  &:hover {
    color: var(--primaryDark);
    background: color-mix(in srgb, var(--primary) 8%, transparent);
  }
`;

const PageHeader = styled.header`
  margin-bottom: 1.5rem;
`;

const PageTitle = styled.h1`
  margin: 0 0 0.35rem;
  font-size: clamp(1.5rem, 2.5vw, 1.85rem);
  font-weight: 700;
  letter-spacing: -0.02em;
  color: var(--text);
`;

const PageSubtitle = styled.p`
  margin: 0;
  font-size: 0.9375rem;
  line-height: 1.5;
  color: var(--muted);
  max-width: 52ch;
`;

const FormCard = styled.div`
  background: var(--surface);
  border: 1px solid color-mix(in srgb, var(--outline) 85%, transparent);
  border-radius: 16px;
  padding: 0;
  box-shadow:
    0 1px 2px color-mix(in srgb, var(--onSurface) 6%, transparent),
    0 24px 48px -28px color-mix(in srgb, var(--primary) 18%, rgba(0, 0, 0, 0.35));
  overflow: hidden;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
`;

const Section = styled.section`
  padding: 1.35rem 1.5rem;
  border-bottom: 1px solid color-mix(in srgb, var(--outline) 70%, transparent);
  &:last-of-type {
    border-bottom: none;
  }
  @media (min-width: 640px) {
    padding: 1.5rem 1.75rem;
  }
`;

const SectionHead = styled.div`
  margin-bottom: 1.15rem;
`;

const SectionTitle = styled.h2`
  margin: 0 0 0.25rem;
  font-size: 0.8125rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--onSurfaceVariant, var(--muted));
`;

const SectionLead = styled.p`
  margin: 0;
  font-size: 0.875rem;
  line-height: 1.45;
  color: var(--muted);
`;

const FieldsGrid = styled.div`
  display: grid;
  gap: 1rem 1.25rem;
  @media (min-width: 560px) {
    grid-template-columns: 1fr 1fr;
    & > [data-span='full'] {
      grid-column: 1 / -1;
    }
  }
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  min-width: 0;
`;

const Label = styled.label`
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--text);
`;

const Hint = styled.span`
  font-size: 0.75rem;
  font-weight: 500;
  color: var(--muted);
  line-height: 1.4;
`;

const controlStyles = `
  width: 100%;
  box-sizing: border-box;
  font-family: inherit;
  font-size: 0.9375rem;
  line-height: 1.4;
  padding: 0.65rem 0.85rem;
  border: 1px solid var(--outline);
  border-radius: 10px;
  background: var(--surface);
  color: var(--text);
  transition: border-color 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;
  &:hover {
    border-color: color-mix(in srgb, var(--primary) 35%, var(--outline));
  }
  &:focus {
    outline: none;
    border-color: color-mix(in srgb, var(--primary) 55%, var(--outline));
    box-shadow: 0 0 0 3px color-mix(in srgb, var(--primary) 20%, transparent);
  }
  &::placeholder {
    color: var(--muted);
    opacity: 0.75;
  }
`;

const Input = styled.input`
  ${controlStyles}
`;

const Textarea = styled.textarea`
  ${controlStyles}
  min-height: 9rem;
  resize: vertical;
`;

const Select = styled.select`
  ${controlStyles}
  cursor: pointer;
`;

const FileUploadLabel = styled.label`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  min-height: 5.5rem;
  padding: 1rem 1.25rem;
  border: 1px dashed color-mix(in srgb, var(--primary) 38%, var(--outline));
  border-radius: 12px;
  background: color-mix(in srgb, var(--surfaceVariant, var(--outline)) 35%, var(--surface));
  cursor: pointer;
  transition:
    border-color 0.15s ease,
    background 0.15s ease,
    box-shadow 0.15s ease;
  &:hover {
    border-color: color-mix(in srgb, var(--primary) 55%, var(--outline));
    background: color-mix(in srgb, var(--primary) 6%, var(--surface));
  }
  &:focus-within {
    box-shadow: 0 0 0 3px color-mix(in srgb, var(--primary) 18%, transparent);
    border-color: color-mix(in srgb, var(--primary) 50%, var(--outline));
  }
`;

const OverlayFileInput = styled.input`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
  font-size: 0;
`;

const FileUploadTitle = styled.span`
  position: relative;
  z-index: 0;
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--text);
  pointer-events: none;
`;

const FileUploadMeta = styled.span`
  position: relative;
  z-index: 0;
  font-size: 0.75rem;
  color: var(--muted);
  text-align: center;
  pointer-events: none;
`;

const PointTileBody = styled.div`
  position: relative;
`;

const ThumbnailFrame = styled.div`
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid color-mix(in srgb, var(--outline) 80%, transparent);
  background: var(--surfaceVariant, color-mix(in srgb, var(--outline) 40%, var(--surface)));
  aspect-ratio: 16 / 9;
  max-height: 220px;
  margin-bottom: 0.75rem;
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

const QuickAddList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.45rem;
  margin-top: 0.25rem;
`;

const QuickAddButton = styled.button`
  appearance: none;
  border: 1px solid color-mix(in srgb, var(--outline) 90%, var(--primary));
  background: color-mix(in srgb, var(--surfaceVariant, var(--outline)) 50%, var(--surface));
  color: var(--text);
  border-radius: 9999px;
  padding: 0.3rem 0.85rem;
  cursor: pointer;
  font-size: 0.8125rem;
  font-weight: 500;
  transition:
    background 0.15s ease,
    border-color 0.15s ease,
    transform 0.12s ease;
  &:hover {
    background: color-mix(in srgb, var(--primary) 10%, var(--surface));
    border-color: color-mix(in srgb, var(--primary) 45%, var(--outline));
  }
  &:active {
    transform: scale(0.98);
  }
`;

const MediaTileGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
`;

const MediaTile = styled.div`
  position: relative;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid color-mix(in srgb, var(--outline) 75%, transparent);
  box-shadow: 0 4px 14px color-mix(in srgb, var(--onSurface) 8%, transparent);
  background: var(--surfaceVariant, color-mix(in srgb, var(--outline) 35%, var(--surface)));
`;

const MediaTileImg = styled.img<{ $w?: number; $h?: number }>`
  display: block;
  width: ${({ $w }) => $w ?? 128}px;
  height: ${({ $h }) => $h ?? 72}px;
  object-fit: cover;
`;

const TileRemove = styled.button`
  position: absolute;
  top: 6px;
  right: 6px;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 9999px;
  cursor: pointer;
  font-size: 1rem;
  line-height: 1;
  color: #fff;
  background: color-mix(in srgb, var(--error, #dc2626) 92%, #000);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
  transition: transform 0.12s ease, background 0.12s ease;
  &:hover {
    background: var(--error, #b91c1c);
    transform: scale(1.05);
  }
`;

const PointBadge = styled.span`
  position: absolute;
  top: 8px;
  left: 8px;
  z-index: 1;
  min-width: 22px;
  height: 22px;
  padding: 0 7px;
  border-radius: 9999px;
  background: rgba(0, 0, 0, 0.78);
  border: 1px solid rgba(255, 255, 255, 0.18);
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.6875rem;
  font-weight: 700;
  pointer-events: none;
`;

const PointControls = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 0.35rem;
  padding: 0.4rem 0.5rem;
  background: color-mix(in srgb, var(--surface) 88%, var(--surfaceVariant, var(--outline)));
  border-top: 1px solid color-mix(in srgb, var(--outline) 70%, transparent);
`;

const IconButton = styled.button<{ $disabled?: boolean }>`
  flex: 1;
  padding: 0.35rem 0.5rem;
  font-size: 0.8125rem;
  font-weight: 600;
  border: 1px solid var(--outline);
  border-radius: 8px;
  background: var(--surface);
  color: var(--text);
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ $disabled }) => ($disabled ? 0.45 : 1)};
  transition: background 0.12s ease, border-color 0.12s ease;
  &:hover:not(:disabled) {
    background: color-mix(in srgb, var(--primary) 8%, var(--surface));
    border-color: color-mix(in srgb, var(--primary) 35%, var(--outline));
  }
`;

const PdfRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.65rem;
  padding: 0.85rem 1rem;
  border-radius: 10px;
  background: color-mix(in srgb, var(--surfaceVariant, var(--outline)) 40%, var(--surface));
  border: 1px solid color-mix(in srgb, var(--outline) 75%, transparent);
`;

const PdfLink = styled.a`
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--primary);
  text-decoration: none;
  word-break: break-word;
  &:hover {
    text-decoration: underline;
  }
`;

const WarningText = styled.p`
  margin: 0.5rem 0 0;
  font-size: 0.75rem;
  color: #b45309;
  line-height: 1.4;
`;

const SaveBar = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1.15rem 1.5rem 1.35rem;
  background: color-mix(in srgb, var(--surfaceVariant, var(--outline)) 28%, var(--surface));
  border-top: 1px solid color-mix(in srgb, var(--outline) 70%, transparent);
  @media (min-width: 640px) {
    padding: 1.25rem 1.75rem 1.5rem;
  }
`;

const Button = styled.button`
  appearance: none;
  min-width: 8.5rem;
  padding: 0.7rem 1.35rem;
  border: none;
  border-radius: 11px;
  cursor: pointer;
  font-size: 0.9375rem;
  font-weight: 600;
  font-family: inherit;
  background: linear-gradient(180deg, var(--primary) 0%, var(--primaryDark, var(--primary)) 100%);
  color: var(--onPrimary, #fff);
  box-shadow:
    0 1px 0 color-mix(in srgb, #fff 22%, transparent) inset,
    0 8px 20px color-mix(in srgb, var(--primary) 38%, transparent);
  transition:
    transform 0.12s ease,
    box-shadow 0.15s ease,
    opacity 0.15s ease;
  &:hover:not(:disabled) {
    box-shadow:
      0 1px 0 color-mix(in srgb, #fff 28%, transparent) inset,
      0 10px 26px color-mix(in srgb, var(--primary) 45%, transparent);
  }
  &:active:not(:disabled) {
    transform: translateY(1px);
  }
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
    box-shadow: none;
  }
`;

const DangerButton = styled.button`
  appearance: none;
  padding: 0.45rem 0.85rem;
  font-size: 0.8125rem;
  font-weight: 600;
  font-family: inherit;
  border-radius: 8px;
  cursor: pointer;
  border: 1px solid color-mix(in srgb, var(--error, #dc2626) 45%, transparent);
  background: color-mix(in srgb, var(--error, #dc2626) 12%, var(--surface));
  color: var(--error, #b91c1c);
  transition: background 0.12s ease;
  &:hover {
    background: color-mix(in srgb, var(--error, #dc2626) 20%, var(--surface));
  }
`;

const QUICK_SPEAKERS = ['Bishop Russ & Pastor Meka'];

export const SermonFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const editing = Boolean(id);
  const navigate = useNavigate();

  const { data: seriesList } = useSermonSeries();
  const { data: sermons } = useSermons();
  const existing = sermons?.find((s) => s.id === id);

  const [title, setTitle] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [date, setDate] = useState('');
  const [speaker, setSpeaker] = useState('');
  const [status, setStatus] = useState('draft');
  const [seriesId, setSeriesId] = useState<string | ''>('');
  const [thumbnailUrl, setThumbnailUrl] = useState('');
  const [videoUrl, setVideoUrl] = useState('');
  const [videoUrlSecondary, setVideoUrlSecondary] = useState('');
  const [notes, setNotes] = useState('');
  const [confessionImages, setConfessionImages] = useState<string[]>([]);
  const [notesPdf, setNotesPdf] = useState<{ id: string; url: string; title?: string } | null>(null);
  const [pointsImages, setPointsImages] = useState<{ id: string; url: string; sortOrder?: number }[]>([]);
  const [supportsPointOrdering, setSupportsPointOrdering] = useState(true);

  const { data: assets } = useSermonAssets(editing ? id : undefined);

  // Pre-fill form values when editing
  useEffect(() => {
    if (existing) {
      setTitle(existing.title || '');
      setSubtitle(existing.subtitle || '');
      setDate(existing.date || '');
      setSpeaker(existing.speaker || '');
      // Normalize legacy "published" to new "active" status for the dropdown
      setStatus(existing.status === 'published' ? 'active' : existing.status || 'draft');
      setSeriesId(existing.series_id || '');
      setThumbnailUrl(existing.thumbnail_url || '');
      setVideoUrl(existing.video_url || '');
      setVideoUrlSecondary(existing.video_url_secondary || '');
      setNotes(existing.notes || '');
    }
  }, [existing]);

  // Load assets when editing
  useEffect(() => {
    if (assets) {
      setNotes(assets.notes || '');
      setConfessionImages(assets.confessionImages);
      setSupportsPointOrdering(Boolean(assets.supportsPointOrdering));
      // convert points to id/url via extra query
    }
  }, [assets]);

  // add function to fetch points docs when editing
  useEffect(() => {
    const loadPoints = async () => {
      if (editing && id) {
        let supportsOrdering = true;
        let docs: any[] = [];
        const { data, error } = await supabase
          .from('sermon_documents')
          .select('id, file_url, file_type, sort_order, created_at')
          .eq('sermon_id', id);

        if (error) {
          supportsOrdering = false;
          const fallback = await supabase
            .from('sermon_documents')
            .select('id, file_url, file_type, created_at')
            .eq('sermon_id', id);
          if (fallback.error || !fallback.data) {
            return;
          }
          docs = fallback.data;
        } else {
          docs = data ?? [];
        }

        const sorted = [...docs].sort((a, b) => {
          const aOrder = typeof a.sort_order === 'number' ? a.sort_order : Number.MAX_SAFE_INTEGER;
          const bOrder = typeof b.sort_order === 'number' ? b.sort_order : Number.MAX_SAFE_INTEGER;
          if (aOrder !== bOrder) return aOrder - bOrder;
          return (a.created_at || '').localeCompare(b.created_at || '');
        });

        const imgs = sorted
          .filter((d: any) => (d.file_type || '').toLowerCase().startsWith('image/'))
          .map((d: any) => ({ id: d.id, url: d.file_url, sortOrder: d.sort_order }));

        const pdfDoc = sorted.find((d: any) => (d.file_type || '').toLowerCase().includes('pdf'));
        setNotesPdf(
          pdfDoc ? { id: pdfDoc.id, url: pdfDoc.file_url, title: pdfDoc.title } : null
        );

        setSupportsPointOrdering(supportsOrdering);
        setPointsImages(imgs);
      }
    };
    loadPoints();
  }, [editing, id]);

  // modify handleUpload etc? We'll add uploadConfession, uploadPoint

  const uploadConfession = async (file: File) => {
    const fileExt = file.name.split('.').pop();
    const fileName = `conf-${Date.now()}.${fileExt}`;
    const { error } = await supabase.storage.from('post-images').upload(fileName, file, { upsert: false });
    if (error) throw error;
    const { data } = supabase.storage.from('post-images').getPublicUrl(fileName);
    setConfessionImages((prev) => [...prev, data.publicUrl]);
  };

  const uploadPointImage = async (file: File) => {
    if (!id) {
      alert('Save sermon first before uploading points');
      return;
    }
    const fileExt = file.name.split('.').pop();
    const fileName = `point-${Date.now()}.${fileExt}`;
    const { error } = await supabase.storage.from('post-images').upload(fileName, file, { upsert: false });
    if (error) throw error;
    const { data } = supabase.storage.from('post-images').getPublicUrl(fileName);
    // insert row
    const nextOrder = pointsImages.length;
    const insertPayload: any = { title: file.name, file_url: data.publicUrl, file_type: file.type, sermon_id: id };
    if (supportsPointOrdering) insertPayload.sort_order = nextOrder;
    const { data: row, error: insertErr } = await supabase
      .from('sermon_documents')
      .insert(insertPayload)
      .select()
      .single();
    if (insertErr) throw insertErr;
    setPointsImages((prev) => [...prev, { id: row.id, url: row.file_url, sortOrder: insertPayload.sort_order }]);
  };

  const removePoint = async (docId: string) => {
    await supabase.from('sermon_documents').delete().eq('id', docId);
    setPointsImages((prev) => {
      const next = prev.filter((p) => p.id !== docId);
      persistPointsOrder(next);
      return next;
    });
  };

  const persistPointsOrder = async (items: { id: string; url: string; sortOrder?: number }[]) => {
    setPointsImages(items);
    if (!supportsPointOrdering) return;
    try {
      await Promise.all(
        items.map((p, index) =>
          supabase.from('sermon_documents').update({ sort_order: index }).eq('id', p.id)
        )
      );
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to save points order', err);
      alert('Could not save the new order. Please try again.');
    }
  };

  const movePoint = (idToMove: string, direction: -1 | 1) => {
    setPointsImages((prev) => {
      const idx = prev.findIndex((p) => p.id === idToMove);
      if (idx === -1) return prev;
      const newIdx = idx + direction;
      if (newIdx < 0 || newIdx >= prev.length) return prev;
      const updated = [...prev];
      const [removed] = updated.splice(idx, 1);
      updated.splice(newIdx, 0, removed);
      persistPointsOrder(updated);
      return updated;
    });
  };

  const removeConfession = (url: string) => {
    setConfessionImages((prev) => prev.filter((u) => u !== url));
  };

  const uploadNotesPdf = async (file: File) => {
    if (!id) {
      alert('Save sermon first before uploading the Notes PDF.');
      return;
    }
    const fileExt = file.name.split('.').pop() || 'pdf';
    const fileName = `sermon-notes-${Date.now()}.${fileExt}`;
    const { error } = await supabase.storage
      .from('post-images')
      .upload(fileName, file, { upsert: false, contentType: file.type });
    if (error) throw error;
    const { data } = supabase.storage.from('post-images').getPublicUrl(fileName);
    const insertPayload: any = {
      sermon_id: id,
      file_url: data.publicUrl,
      file_type: file.type,
      title: file.name,
    };
    if (supportsPointOrdering) insertPayload.sort_order = -1; // keep PDF before points
    const { data: row, error: insertErr } = await supabase
      .from('sermon_documents')
      .insert(insertPayload)
      .select()
      .single();
    if (insertErr) throw insertErr;
    setNotesPdf({ id: row.id, url: row.file_url, title: row.title });
  };

  const removeNotesPdf = async () => {
    if (!notesPdf) return;
    await supabase.from('sermon_documents').delete().eq('id', notesPdf.id);
    setNotesPdf(null);
  };

  const upsert = useUpsertSermon();

  const handleUpload = async (file: File) => {
    const fileExt = file.name.split('.').pop();
    const fileName = `${Date.now()}.${fileExt}`;
    const { error } = await supabase.storage
      .from('post-images')
      .upload(fileName, file, { upsert: false });
    if (error) throw error;
    const { data } = supabase.storage.from('post-images').getPublicUrl(fileName);
    setThumbnailUrl(data.publicUrl);
  };

  const handleVideoUpload = async (file: File) => {
    const fileExt = file.name.split('.').pop();
    const fileName = `${Date.now()}.${fileExt}`;
    const { error } = await supabase.storage.from('sermon-videos').upload(fileName, file, {
      upsert: false,
      cacheControl: '3600',
      contentType: file.type,
    });
    if (error) throw error;
    const { data } = supabase.storage.from('sermon-videos').getPublicUrl(fileName);
    setVideoUrl(data.publicUrl);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const payload = {
      id,
      title: title.trim(),
      subtitle: subtitle.trim() || '',
      date: date || undefined,
      speaker: speaker.trim() || undefined,
      status: status || 'draft',
      series_id: seriesId || null,
      thumbnail_url: thumbnailUrl || null,
      video_url: videoUrl || null,
      video_url_secondary: videoUrlSecondary || null,
      notes: notes.trim() || null,
      confession_image_urls: confessionImages,
    };

    try {
      await upsert.mutateAsync(payload);
      navigate('/sermons');
    } catch (err: any) {
      // Ensure user sees a helpful error instead of "[object Object]"
      // eslint-disable-next-line no-console
      console.error('Failed to save sermon', err);
      const message = err?.message || 'Failed to save sermon';
      // eslint-disable-next-line no-alert
      alert(message);
    }
  };

  return (
    <Page>
      <PageInner>
        <BackRow>
          <BackLink to="/sermons">
            <span aria-hidden>←</span> Back to sermons
          </BackLink>
        </BackRow>
        <PageHeader>
          <PageTitle>{editing ? 'Edit sermon' : 'New sermon'}</PageTitle>
          <PageSubtitle>
            {editing
              ? 'Update details, media, and study materials. Changes apply after you save.'
              : 'Add sermon metadata first; you can upload points and PDFs after the sermon is saved.'}
          </PageSubtitle>
        </PageHeader>

        <FormCard>
          <Form onSubmit={handleSubmit}>
            <Section>
              <SectionHead>
                <SectionTitle>Basics</SectionTitle>
                <SectionLead>Title, schedule, speaker, and how this sermon appears in the app.</SectionLead>
              </SectionHead>
              <FieldsGrid>
                <Field data-span="full">
                  <Label htmlFor="sermon-title">Title</Label>
                  <Input
                    id="sermon-title"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    required
                    placeholder="e.g. Walking in Faith"
                  />
                </Field>
                <Field data-span="full">
                  <Label htmlFor="sermon-subtitle">Subtitle</Label>
                  <Input
                    id="sermon-subtitle"
                    value={subtitle}
                    onChange={(e) => setSubtitle(e.target.value)}
                    placeholder="Optional supporting line"
                  />
                </Field>
                <Field>
                  <Label htmlFor="sermon-date">Date</Label>
                  <Input id="sermon-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
                </Field>
                <Field>
                  <Label htmlFor="sermon-status">Status</Label>
                  <Select id="sermon-status" value={status} onChange={(e) => setStatus(e.target.value)}>
                    <option value="draft">Draft</option>
                    <option value="active">Active</option>
                  </Select>
                </Field>
                <Field data-span="full">
                  <Label htmlFor="sermon-speaker">Speaker</Label>
                  <Input
                    id="sermon-speaker"
                    value={speaker}
                    onChange={(e) => setSpeaker(e.target.value)}
                    placeholder="Name as it should appear"
                  />
                  <QuickAddList>
                    {QUICK_SPEAKERS.map((name) => (
                      <QuickAddButton key={name} type="button" onClick={() => setSpeaker(name)}>
                        {name}
                      </QuickAddButton>
                    ))}
                  </QuickAddList>
                </Field>
                <Field data-span="full">
                  <Label htmlFor="sermon-series">Series</Label>
                  <Select id="sermon-series" value={seriesId} onChange={(e) => setSeriesId(e.target.value)}>
                    <option value="">None</option>
                    {seriesList?.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.title}
                      </option>
                    ))}
                  </Select>
                </Field>
              </FieldsGrid>
            </Section>

            <Section>
              <SectionHead>
                <SectionTitle>Media</SectionTitle>
                <SectionLead>Thumbnail, primary and secondary video links, or upload a video file.</SectionLead>
              </SectionHead>
              <FieldsGrid>
                <Field data-span="full">
                  <Label>Thumbnail</Label>
                  {thumbnailUrl ? (
                    <ThumbnailFrame>
                      <img src={thumbnailUrl} alt="Sermon thumbnail preview" />
                    </ThumbnailFrame>
                  ) : null}
                  <FileUploadLabel>
                    <OverlayFileInput
                      type="file"
                      accept="image/*"
                      onChange={async (e) => {
                        if (e.target.files?.[0]) {
                          try {
                            await handleUpload(e.target.files[0]);
                          } catch {
                            // eslint-disable-next-line no-alert
                            alert('Upload failed');
                          }
                        }
                        e.target.value = '';
                      }}
                    />
                    <FileUploadTitle>Upload image</FileUploadTitle>
                    <FileUploadMeta>PNG or JPG — shown as the sermon cover</FileUploadMeta>
                  </FileUploadLabel>
                </Field>
                <Field data-span="full">
                  <Label htmlFor="video-primary">Video URL (Sunday worship)</Label>
                  <Input
                    id="video-primary"
                    value={videoUrl}
                    onChange={(e) => setVideoUrl(e.target.value)}
                    placeholder="https://…"
                  />
                </Field>
                <Field data-span="full">
                  <Label htmlFor="video-secondary">Secondary video URL (Wednesday worship)</Label>
                  <Input
                    id="video-secondary"
                    value={videoUrlSecondary}
                    onChange={(e) => setVideoUrlSecondary(e.target.value)}
                    placeholder="Optional"
                  />
                </Field>
                <Field data-span="full">
                  <Label>Or upload video file</Label>
                  <FileUploadLabel>
                    <OverlayFileInput
                      type="file"
                      accept="video/*"
                      onChange={async (e) => {
                        if (e.target.files?.[0]) {
                          try {
                            await handleVideoUpload(e.target.files[0]);
                          } catch {
                            // eslint-disable-next-line no-alert
                            alert('Video upload failed');
                          }
                        }
                        e.target.value = '';
                      }}
                    />
                    <FileUploadTitle>Choose video file</FileUploadTitle>
                    <FileUploadMeta>Stored in sermon video bucket; sets primary URL</FileUploadMeta>
                  </FileUploadLabel>
                </Field>
              </FieldsGrid>
            </Section>

            <Section>
              <SectionHead>
                <SectionTitle>Notes &amp; PDF</SectionTitle>
                <SectionLead>Markdown notes for the app and an optional study PDF.</SectionLead>
              </SectionHead>
              <FieldsGrid>
                <Field data-span="full">
                  <Label htmlFor="sermon-notes">Notes (Markdown)</Label>
                  <Textarea id="sermon-notes" rows={6} value={notes} onChange={(e) => setNotes(e.target.value)} />
                </Field>
                <Field data-span="full">
                  <Label>Notes PDF (study lesson)</Label>
                  {!editing && (
                    <Hint>Save the sermon first, then you can attach a PDF here.</Hint>
                  )}
                  {notesPdf ? (
                    <PdfRow>
                      <PdfLink href={notesPdf.url} target="_blank" rel="noopener noreferrer">
                        {notesPdf.title || 'Notes PDF'}
                      </PdfLink>
                      <DangerButton type="button" onClick={removeNotesPdf}>
                        Remove PDF
                      </DangerButton>
                    </PdfRow>
                  ) : null}
                  {editing && (
                    <FileUploadLabel style={{ marginTop: notesPdf ? '0.75rem' : 0 }}>
                      <OverlayFileInput
                        type="file"
                        accept="application/pdf,.pdf"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            try {
                              await uploadNotesPdf(file);
                            } catch {
                              // eslint-disable-next-line no-alert
                              alert('Notes PDF upload failed.');
                            }
                          }
                          e.target.value = '';
                        }}
                      />
                      <FileUploadTitle>{notesPdf ? 'Replace PDF' : 'Upload PDF'}</FileUploadTitle>
                      <FileUploadMeta>One PDF per sermon</FileUploadMeta>
                    </FileUploadLabel>
                  )}
                </Field>
              </FieldsGrid>
            </Section>

            <Section>
              <SectionHead>
                <SectionTitle>Confession images</SectionTitle>
                <SectionLead>Slides or images used during confession.</SectionLead>
              </SectionHead>
              <MediaTileGrid>
                {confessionImages.map((url, index) => (
                  <MediaTile key={url}>
                    <MediaTileImg src={url} alt={`Confession ${index + 1}`} $w={128} $h={72} />
                    <TileRemove type="button" onClick={() => removeConfession(url)} aria-label="Remove image">
                      ×
                    </TileRemove>
                  </MediaTile>
                ))}
              </MediaTileGrid>
              <FileUploadLabel style={{ marginTop: '0.85rem' }}>
                <OverlayFileInput
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={async (e) => {
                    if (e.target.files) {
                      for (const f of Array.from(e.target.files)) {
                        try {
                          await uploadConfession(f as File);
                        } catch {
                          // eslint-disable-next-line no-alert
                          alert('Confession upload failed');
                        }
                      }
                      e.target.value = '';
                    }
                  }}
                />
                <FileUploadTitle>Add images</FileUploadTitle>
                <FileUploadMeta>You can select multiple files</FileUploadMeta>
              </FileUploadLabel>
            </Section>

            <Section>
              <SectionHead>
                <SectionTitle>Point images</SectionTitle>
                <SectionLead>Ordered slides for sermon points. Reorder with the arrows when supported.</SectionLead>
              </SectionHead>
              {!editing && <Hint style={{ display: 'block', marginBottom: '0.75rem' }}>Save the sermon first to upload point images.</Hint>}
              <MediaTileGrid>
                {pointsImages.map((p, index) => (
                  <MediaTile key={p.id}>
                    <PointTileBody>
                      <PointBadge>{index + 1}</PointBadge>
                      <MediaTileImg src={p.url} alt={`Point ${index + 1}`} $w={192} $h={108} />
                      <TileRemove type="button" onClick={() => removePoint(p.id)} aria-label="Remove point image">
                        ×
                      </TileRemove>
                    </PointTileBody>
                    <PointControls>
                      <IconButton
                        type="button"
                        onClick={() => movePoint(p.id, -1)}
                        disabled={!supportsPointOrdering || index === 0}
                        $disabled={!supportsPointOrdering || index === 0}
                      >
                        Move up
                      </IconButton>
                      <IconButton
                        type="button"
                        onClick={() => movePoint(p.id, 1)}
                        disabled={!supportsPointOrdering || index === pointsImages.length - 1}
                        $disabled={!supportsPointOrdering || index === pointsImages.length - 1}
                      >
                        Move down
                      </IconButton>
                    </PointControls>
                  </MediaTile>
                ))}
              </MediaTileGrid>
              {!supportsPointOrdering && (
                <WarningText>Reordering requires the `sort_order` column on sermon_documents.</WarningText>
              )}
              {editing && (
                <FileUploadLabel style={{ marginTop: '0.85rem' }}>
                  <OverlayFileInput
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={async (e) => {
                      if (e.target.files) {
                        for (const f of Array.from(e.target.files)) {
                          try {
                            await uploadPointImage(f as File);
                          } catch {
                            // eslint-disable-next-line no-alert
                            alert('Point upload failed');
                          }
                        }
                        e.target.value = '';
                      }
                    }}
                  />
                  <FileUploadTitle>Add point images</FileUploadTitle>
                  <FileUploadMeta>Multiple selection allowed</FileUploadMeta>
                </FileUploadLabel>
              )}
            </Section>

            <SaveBar>
              <Button type="submit" disabled={upsert.isPending}>
                {upsert.isPending ? 'Saving…' : 'Save sermon'}
              </Button>
            </SaveBar>
          </Form>
        </FormCard>
      </PageInner>
    </Page>
  );
};

export default SermonFormPage;
